import {combineEpics, Epic} from 'redux-observable';
import {from, of} from 'rxjs';
import {
  switchMap,
  map,
  startWith,
  catchError,
  filter
} from 'rxjs/operators';
import {isOfType} from 'typesafe-actions';

import {
  TableauActions,
  TableauActionTypes,
  loadingViews,
  loadedViews,
  loadingViewsFailed,
  loadingWorkbooks,
  loadedWorkbooks,
  loadingWorkbooksFailed,
  loadingView,
  loadedView,
  loadingViewFailed,
  loadCustomViews,
  loadingCustomViews,
  loadedCustomViews,
  loadingCustomViewsFailed,
  requestTrustedTicketLoading,
  requestTrustedTicketSuccess,
  requestTrustedTicketFailed,
  uploadThumbnailFailed,
  uploadThumbnailSuccess,
  uploadingThumbnail,
  loadedViewData,
  loadingViewData,
  loadingViewDataFailed,
  uploadThumbnailProgress,
} from '../actions/tableauActions';
import {IState} from '../reducers';
import {
  getViews,
  getWorkbooks,
  getView,
  getCustomViews,
  getTrustedTicket,
  uploadThumbnail,
  getViewData,
} from '../../services/tableauApi';
import config from '../../../config';

const buildErrorMessage = (error: any, action: any, prefix: string) => {
  const err = error.response?.data?.message || "";
  const link = `${config.app.url}/d/i/${action.payload.integrationUid}`;
  const htmlLink = `<a href=${link} target="_blank">here</a>\n`;
  const cleanedErr = err.replace(/\n\n/g, '\n').replace(/\t/g, '');
  const suffix = '\nPlease go and edit the integration ' + htmlLink;
  const message = `\n${prefix} ${cleanedErr} ${suffix}`;
  return [
    {
      block_uid: action.payload.blockUid,
      status: 'failed',
      value: {
        response_json: {
          error: message,
          code: error.response?.status
        }
      }
    }
  ];
}

const loadViewEpic: Epic<TableauActions, TableauActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(TableauActionTypes.LOAD_VIEW)),
    switchMap((action: any) =>
      from(getView(action.payload.integrationUid, action.payload.viewId)).pipe(
        map((response: any) => loadedView(response.result, action.payload.blockUid, action.payload.integrationUid)),
        startWith(loadingView()),
        catchError((error: any) => {
          const prefix = 'Unable to load the Tableau View.';
          const resultError = buildErrorMessage(error, action, prefix);

          return of(loadingViewFailed(resultError));
        })
      )
    )
  );

const loadViewDataEpic: Epic<TableauActions, TableauActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(TableauActionTypes.LOAD_VIEW_DATA)),
    switchMap((action: any) =>
      from(getViewData(action.payload.integrationUid, action.payload.viewId, action.payload.action)).pipe(
        map((response: any) => loadedViewData(response.result, action.payload.blockUid, action.payload.integrationUid)),
        startWith(loadingViewData()),
        catchError((error: any) => {
          const prefix = 'Unable to load the Tableau View Data.';
          const resultError = buildErrorMessage(error, action, prefix);

          return of(loadingViewDataFailed(resultError));
        })
      )
    )
  );

const loadViewsEpic: Epic<TableauActions, TableauActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(TableauActionTypes.LOAD_VIEWS)),
    switchMap((action: any) =>
      from(getViews(
        action.payload.integrationUid,
        action.payload.workbookId,
        action.payload.keyword)
      ).pipe(
        map((response: any) => loadedViews(
          response.results,
          action.payload.blockUid,
          action.payload.integrationUid,
          action.payload.workbookId,
        )),
        startWith(loadingViews()),
        catchError((error: any) => {
          const prefix = 'Unable to load the Tableau Views.';
          const resultError = buildErrorMessage(error, action, prefix);

          return of(loadingViewsFailed(resultError));
        })
      )
    )
  );

const loadWorkbooksEpic: Epic<TableauActions, TableauActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(TableauActionTypes.LOAD_WORKBOOKS)),
    switchMap((action: any) =>
      from(getWorkbooks(action.payload.integrationUid, action.payload.keyword)).pipe(
        map((response: any) => {
          if (action.payload.loadCustomViews) {
            /*
             * In order to avoid a racing condition on the BE with loadWorkbooks and loadCustomViews
             *   being called at the same time and both trying to update `auth_token` resulting in a 401 auth error,
             *   we will call "custom views" after workbooks are loaded, if `loadCustomViews` is set to true
             *   (sequential loading instead of parallel).
             */
            dispatch(loadCustomViews(action.payload.blockUid, action.payload.integrationUid));
          }
          return loadedWorkbooks(
            response.results,
            action.payload.blockUid,
            action.payload.integrationUid,
          )
        }),
        startWith(loadingWorkbooks()),
        catchError((error: any) => {
          const prefix = 'Unable to load the Tableau Workbooks.';
          const resultError = buildErrorMessage(error, action, prefix);
          return of(loadingWorkbooksFailed(resultError));
        })
      )
    )
  );

const loadCustomViewsEpic: Epic<TableauActions, TableauActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(TableauActionTypes.LOAD_CUSTOM_VIEWS)),
    switchMap((action: any) =>
      from(getCustomViews(action.payload.integrationUid)
      ).pipe(
        map((response: any) => loadedCustomViews(
          response.results,
          action.payload.blockUid,
          action.payload.integrationUid
        )),
        startWith(loadingCustomViews()),
        catchError((error: any) => {
          return of(loadingCustomViewsFailed());
        })
      )
    )
  );


const requestTrustedTicketEpic: Epic<TableauActions, TableauActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(TableauActionTypes.REQUEST_TRUSTED_TICKET)),
    switchMap((action: any) =>
      from(getTrustedTicket(action.payload.integrationUid)).pipe(
        map((response: any) => requestTrustedTicketSuccess(action.payload.integrationUid, response.result)),
        startWith(requestTrustedTicketLoading()),
        catchError((error: any) => {
          return of(requestTrustedTicketFailed(error));
        })
      )
    )
  );

const uploadThumbnailEpic: Epic<TableauActions, TableauActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(TableauActionTypes.UPLOAD_THUMBNAIL)),
    switchMap((action: any) => {
        const { integrationUid, blockUid, workspaceId, startTime, file } = action.payload;

        const onProgress = (progressEvent: ProgressEvent) => {
          let percent = 0;
          if (progressEvent.total > 0) {
            percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          }
          dispatch(uploadThumbnailProgress(percent));
        };

        return from(uploadThumbnail(integrationUid, blockUid, workspaceId, startTime, file, onProgress)).pipe(
          map((response: any) => uploadThumbnailSuccess(response.result)),
          startWith(uploadingThumbnail()),
          catchError((error: any) => {
            const apiMessage = error?.response?.data?.message || error.message || "Unknown error";
            return of(uploadThumbnailFailed(apiMessage));
          })
        )
      }
    )
  );


export default combineEpics(
  loadViewEpic,
  loadViewsEpic,
  loadWorkbooksEpic,
  loadCustomViewsEpic,
  requestTrustedTicketEpic,
  uploadThumbnailEpic,
  loadViewDataEpic,
);
